import * as React from "react";
import {Card, Col, Row} from "react-bootstrap";
import {FullUser, UserProvider} from "../../types/adminstate";
import {isSuperAdmin, transformDataParams} from "../../utils/commons.ts";
import {
    KColumnAction,
    KColumnProps,
    KContainer,
    KTableColumnType,
    KTableFilter,
    KTableFixedSelectionFilter,
    KTableFreeTextFilter,
    KTableGlobalActionType,
    KTableLoader,
    Translate,
    useKTable,
    Visibility
} from "@kopjra/uikit";
import {useGetUserInfo, useGetUsersQuery} from "../../api/user.ts";
import {Routes} from "../../utils/router.tsx";
import {useTranslation} from "react-i18next";

export const Users: React.FC = () => {
    const tableName = "admin/users/list";

    const {Table, dataParams} = useKTable<FullUser>({id: tableName});
    const {data: users, refetch} = useGetUsersQuery(transformDataParams(dataParams), {pollingInterval: 10000});

    const {data: userInfo} = useGetUserInfo();
    
    const {t} = useTranslation();

    const actions: KColumnAction<FullUser>[] = [
        {
            label: <><i className="fal fa-folder-open action-icon"/> <Translate i18nKey="admin.users.table.actionOpenSessions"/></>,
            handler: async (datum) =>  {
                const restOfTheParameters = `${datum.firstname ? `&userName=${datum.firstname}` : ""}${datum.lastname ? `&userLastname=${datum.lastname}` : ""}${datum.email ? `&userEmail=${datum.email}` : ""}`;
                Routes.goAdmin_sessions(`${datum.userId}${restOfTheParameters}`);
            }
        }
    ];

    const globalActions: KTableGlobalActionType<FullUser>[] = [
        {
            bulk: false,
            handler: async () => {refetch()},
            label: <i className="fal fa-sync action-icon"/>,
        },
    ];
    const filters: KTableFilter<FullUser>[] = [
        new KTableFreeTextFilter("firstname", t("admin.users.table.name"), false),
        new KTableFreeTextFilter("lastname", t("admin.users.table.lastname"), false),
        new KTableFreeTextFilter("email", t("admin.users.table.email"), false),
    ];
    if (isSuperAdmin(userInfo)) {
        filters.push(new KTableFixedSelectionFilter("provider", t("admin.users.table.provider"), Object.values(UserProvider).map((key) => ({
            label: t(`admin.users.providers.${key}`),
            value: key,
        }))));
    }
    const columns: KColumnProps<FullUser>[] = [
        {type: KTableColumnType.FIELD, field: "firstname", label: t("admin.users.table.name"), colSpan: 2, alignment: "left"},
        {type: KTableColumnType.FIELD, field: "lastname", label: t("admin.users.table.lastname"), colSpan: 2, alignment: "left"},
        {type: KTableColumnType.FIELD, field: "email", label: t("admin.users.table.email"), colSpan: isSuperAdmin(userInfo) ? 2 : 4, alignment: "left"},
        {type: KTableColumnType.FIELD, field: "crmid", label: t("admin.users.table.crmid"), colSpan: 1, alignment: "right"},
        {type: KTableColumnType.FIELD, field: "consumedCredits", label: t("admin.users.table.consumedCredits"), colSpan: 1, alignment: "right"},
        {type: KTableColumnType.FIELD, field: "externalId", label: t("admin.users.table.externalId"), alignment: "right", initialVisibility: Visibility.HIDDEN},
    ];
    if (isSuperAdmin(userInfo)) {
        columns.push({ type: KTableColumnType.FIELD, field: "provider", label: t("admin.users.table.provider"), colSpan: 2, alignment: "left" });
    }
    columns.push({type: KTableColumnType.ACTIONS, actions, colSpan: 2, alignment: "right"});
    return (
        <KContainer>
            <Row style={{paddingTop: 55}}>
                <Col xs={12}>
                    <Card className="text-center">
                        <Card.Header><Translate i18nKey="admin.users.title"/></Card.Header>
                        <Card.Body>
                            {users ? (
                                <Row style={{paddingTop: 50}}>
                                    <Col>
                                        <Table
                                            data={users}
                                            header={{
                                                globalActions,
                                                columnSelector: true,
                                                filters
                                            }}
                                            body={{
                                                checkboxes: false,
                                                columns,
                                            }}
                                        />
                                    </Col>
                                </Row>
                            ) : (
                                <Row style={{marginTop: 40, marginBottom: 30}}>
                                    <KTableLoader/>
                                </Row>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </KContainer>
    );
};
